import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Splash from "../components/Splash";
import { AboutModal, ScheduleModal } from "../components/Modals";
import ExternalLinks from "../components/ExternalLinks";
import PostCard from "../components/PostCard";
import Logo from "../components/Logo";
import { mediaUrl } from "../lib/api";
import { AudioPlayer, VideoPlayer } from "../components/MediaPlayer";
import { JsonLd, WEBSITE_SCHEMA, PERSON_SCHEMA } from "../components/JsonLd";
import BrandedSection from "../components/BrandedSection";
import NewsletterSubscribe from "../components/NewsletterSubscribe";
import LightningTipModal from "../components/LightningTipModal";
import AdSlot from "../components/AdSlot";
import { useBranding } from "../components/BrandedSection";
import { externalLinksOrDefault } from "../lib/externalLinks";

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function MediaHighlights({ items }) {
  if (!items?.length) return null;
  return (
    <section data-testid="media-highlights" style={{ marginBottom: 24 }}>
      <h2 className="xs-section-title">media highlights</h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}
      >
        {items.map((p) => (
          <div
            key={p.id}
            style={{
              border: "1px solid #ffde16",
              borderRadius: 10,
              padding: 14,
              background: "rgba(0,0,0,0.78)",
            }}
          >
            <div style={{ fontSize: "0.95em", marginBottom: 8, fontWeight: "bold" }}>
              {p.title}
            </div>
            {p.video_url ? (
              <VideoPlayer src={p.video_url} poster={p.cover_image_url} />
            ) : (
              <AudioPlayer src={p.audio_url} />
            )}
            <a
              href={`/articles/${p.slug}`}
              style={{ fontSize: "0.8em", textDecoration: "underline" }}
            >
              read full post →
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [modal, setModal] = useState(null);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const debounced = useDebounced(q, 300);
  const postsAbort = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const { links = [] } = useBranding();
  const hasEnabledLinks = externalLinksOrDefault(links).some((link) => link.enabled !== false);

  // fetch posts reactively
  useEffect(() => {
    postsAbort.current?.abort?.();
    const controller = new AbortController();
    postsAbort.current = controller;
    setLoading(true);
    api
      .get("/posts", { params: { q: debounced || undefined, limit: 30 }, signal: controller.signal })
      .then((r) => setPosts(r.data.items || []))
      .catch((e) => { if (e.name !== "CanceledError") console.error(e); })
      .finally(() => setLoading(false));
  }, [debounced]);

  // media highlights (once)
  useEffect(() => {
    api.get("/media-highlights", { params: { limit: 3 } })
      .then((r) => setHighlights(r.data.items || []))
      .catch(() => {});
  }, []);

  return (
    <div className="App">
      <JsonLd data={WEBSITE_SCHEMA} />
      <JsonLd data={PERSON_SCHEMA} />
      <Splash onReady={() => setReady(true)} />
      <Header />

      <main className="xs-page" data-testid="home-page">
        <div className="xs-container">
          {/* Hero */}
          <section
            style={{
              textAlign: "center",
              paddingTop: 10,
              opacity: ready ? 1 : 0,
              transition: "opacity 0.8s ease",
            }}
            data-testid="hero"
          >
            <div className="xs-hero-lockup" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Logo className="xs-hero-logo" />
              <h1
                style={{
                  color: "#ffde16",
                  fontSize: "clamp(1.6em, 4vw, 2.4em)",
                  margin: "10px 0 2px",
                  letterSpacing: 2,
                  textTransform: "lowercase",
                }}
              >
                xaelserpent
              </h1>
              <div
                style={{ fontSize: "0.95em", opacity: 0.85, letterSpacing: 3, textTransform: "lowercase" }}
              >
                lightning power
              </div>
            </div>

            <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
              <input
                className="xs-search"
                type="search"
                placeholder="search articles, audio, video, tags…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="search-input"
                aria-label="Search"
              />
            </div>
          </section>

          <AdSlot slot="home-top" label="advertisement" style={{ minHeight: 90, maxHeight: 90, padding: "10px 14px" }} />

          {/* Media highlights */}
          {!q && <MediaHighlights items={highlights} />}

          {/* Posts grid */}
          <section style={{ marginTop: 24 }}>
            <h2 className="xs-section-title">
              {q ? `results for "${q}"` : "latest"}
            </h2>
            {loading ? (
              <div style={{ opacity: 0.6, padding: 24 }} data-testid="posts-loading">loading…</div>
            ) : posts.length === 0 ? (
              <div
                style={{ opacity: 0.7, padding: 24, border: "1px dashed #ffde16", borderRadius: 8 }}
                data-testid="posts-empty"
              >
                {q ? "no posts matched your search." : "no posts yet — check back soon."}
              </div>
            ) : (
              <div className="xs-grid" data-testid="posts-grid">
                {posts.map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            )}
          </section>

          {/* External links (original 8) wrapped in branded section */}
          {hasEnabledLinks && <BrandedSection
            style={{
              marginTop: 36,
              padding: "30px 16px 40px",
              borderRadius: 14,
              border: "1px solid rgba(255, 222, 22, 0.35)",
            }}
          >
            <h2 className="xs-section-title" style={{ marginTop: 0 }}>the links</h2>
            <ExternalLinks ready={ready} />
            <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
              <button
                className="xs-link"
                onClick={() => setTipOpen(true)}
                data-testid="home-tip-btn"
                style={{ background: "rgba(255, 222, 22, 0.12)" }}
              >
                <img src="/bolt.png" alt="" className="inline-block h-4 w-4 align-middle" /> tip in sats
              </button>
            </div>
            <div style={{ maxWidth: 480, margin: "30px auto 0" }}>
              <NewsletterSubscribe source="home" />
            </div>
          </BrandedSection>}
        </div>
      </main>

      {modal === "about" && <AboutModal onClose={() => setModal(null)} />}
      {modal === "schedule" && <ScheduleModal onClose={() => setModal(null)} />}
      <LightningTipModal open={tipOpen} onClose={() => setTipOpen(false)} />

      <Footer show={ready} />
    </div>
  );
}
